import { AsyncStatus } from 'lib-shared-legacy-types';
import { handleActions } from 'redux-actions';


import { holdMusicActions } from '../actions/holdMusicActions';

type Audio = {
  id: string | null;
  name: string | null;
  chunk_urls: Array<string>;
  master_file_path: string | null;
  chunk_duration_in_secs: number;
  original_as_mp3_url: string | null;
};

export function emptyAudio(): Audio {
  return {
    name: null,
    chunk_urls: [],
    master_file_path: null,
    chunk_duration_in_secs: 0,
    id: null,
    original_as_mp3_url: null,
  };
}

export type HoldMusicState = {
  meta: {
    uploadAsyncStatus: AsyncStatus;
    getAsyncStatus: AsyncStatus;
  };
  ui: {
    isNewAudioOpen: boolean;
    isHoldingOpen: boolean;
    isHoldModalOpen: boolean;
  };
  allAudios: Array<Audio>;
  selectedAudio: Audio;
  jobIds: Array<string>;
};

const initialState: HoldMusicState = {
  meta: {
    uploadAsyncStatus: 'succeeded',
    getAsyncStatus: 'succeeded',
  },
  ui: {
    isNewAudioOpen: false,
    isHoldingOpen: false,
    isHoldModalOpen: false,
  },
  allAudios: [],
  selectedAudio: emptyAudio(),
  jobIds: [],
};

// Destructure the holdMusic actions.
export const {
  holdMusicGetAll,
  holdMusicGetAllSucceeded,
  holdMusicGetAllFailed,
  holdMusicUpload,
  holdMusicUploadSucceeded,
  holdMusicUploadFailed,
  holdMusicSplit,
  holdMusicSplitSucceeded,
  holdMusicSplitFailed,
  holdMusicSelect,
  holdMusicReset,
  holdMusicShowModal,
  holdMusicHideModal,
  holdMusicWsUploadStatus,
  holdMusicHoldingForSplitingOpen,
  holdMusicHoldingForSplitingClose,
} = holdMusicActions;

const holdMusic = handleActions<HoldMusicState, any>(
  {
    [holdMusicGetAll.toString()]: (state: HoldMusicState) => {
      return { ...state, meta: { ...state.meta, getAsyncStatus: 'loading' } };
    },

    [holdMusicGetAllSucceeded.toString()]: (
      state: HoldMusicState,
      action: any,
    ) => {
      const allAudios: any[] = [];
      for (const wait_music of action.payload.data.wait_music) {
        allAudios.push({
          id: wait_music.uuid,
          name: wait_music.name,
          chunk_urls: wait_music.chunk_urls,
          master_file_path: wait_music.master_file_path,
          original_as_mp3_url: wait_music.original_as_mp3_url,
          chunk_duration_in_secs: wait_music.chunk_duration_in_secs,
        });
      }
      return {
        ...state,
        meta: { ...state.meta, getAsyncStatus: 'succeeded' },
        allAudios: allAudios,
      };
    },

    [holdMusicGetAllFailed.toString()]: (state: HoldMusicState) => {
      return { ...state, meta: { ...state.meta, getAsyncStatus: 'failed' } };
    },

    [holdMusicUpload.toString()]: (state: HoldMusicState) => {
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'loading' },
      };
    },

    [holdMusicUploadSucceeded.toString()]: (state: HoldMusicState) => {
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'succeeded' },
        ui: { ...state.ui, isNewAudioOpen: true },
      };
    },

    [holdMusicUploadFailed.toString()]: (state: HoldMusicState) => {
      return { ...state, meta: { ...state.meta, uploadAsyncStatus: 'failed' } };
    },

    [holdMusicSplit.toString()]: (state: HoldMusicState) => {
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'loading' },
      };
    },

    [holdMusicSplitSucceeded.toString()]: (
      state: HoldMusicState,
      action: any,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'succeeded' },
        ui: { ...state.ui, isNewAudioOpen: true },
        jobIds: [action.payload.data.job_id, ...state.jobIds],
      };
    },

    [holdMusicSplitFailed.toString()]: (state: HoldMusicState) => {
      return { ...state, meta: { ...state.meta, uploadAsyncStatus: 'failed' } };
    },

    [holdMusicWsUploadStatus.toString()]: (
      state: HoldMusicState,
      action: any,
    ) => {
      return {
        ...state,
        ui: { ...state.ui, isHoldingOpen: false, isNewAudioOpen: false },
        jobIds: state.jobIds.filter((jobID) => jobID !== action.payload.job_id),
      };
    },

    [holdMusicSelect.toString()]: (state: HoldMusicState, action: any) => {
      const selectedAudio = Object.assign({}, action.payload.selectedAudio);
      return { ...state, selectedAudio: selectedAudio };
    },

    [holdMusicReset.toString()]: (state: HoldMusicState) => {
      return { ...state, selectedAudio: emptyAudio() };
    },

    [holdMusicShowModal.toString()]: (state: HoldMusicState) => {
      return { ...state, ui: { ...state.ui, isHoldModalOpen: true } };
    },

    [holdMusicHideModal.toString()]: (state: HoldMusicState) => {
      return { ...state, ui: { ...state.ui, isHoldModalOpen: false } };
    },

    [holdMusicHoldingForSplitingOpen.toString()]: (state: HoldMusicState) => {
      return { ...state, ui: { ...state.ui, isHoldingOpen: true } };
    },

    [holdMusicHoldingForSplitingClose.toString()]: (state: HoldMusicState) => {
      return { ...state, ui: { ...state.ui, isHoldingOpen: false } };
    },
  },
  initialState,
);

export default holdMusic;
